import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Cloud, CreditCard, Shield, Brain, Building2, Zap } from "lucide-react";
import { supabase } from "@/lib/supabase";

const capabilities = [
  { icon: Cloud, title: "Cloud Systems", description: "Scalable infrastructure designed for performance and security." },
  { icon: CreditCard, title: "Fintech", description: "Payment systems and banking platforms built for compliance." },
  { icon: Shield, title: "Secure APIs", description: "Enterprise-grade API architectures with comprehensive security." },
  { icon: Brain, title: "AI Applications", description: "Intelligent systems leveraging machine learning." },
  { icon: Building2, title: "Enterprise Software", description: "Custom applications designed for scale." },
  { icon: Zap, title: "Real-time Systems", description: "High-performance data processing pipelines." }
];

const stats = [
  { number: "10+", label: "Projects Delivered" },
  { number: "99.9%", label: "Uptime SLA" },
  { number: "24/7", label: "Support" },
];

export default function HomePage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data, error } = await supabase
          .schema('raindas_technologies_ltd')
          .from('case_study')
          .select()
          .eq('published', true);

        if (error) throw error;
        setCaseStudies(data ?? []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1627674806991-e0dcdb609d73?auto=format&fit=crop&w=2000&q=80"
            alt="Abstract dark technology background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Grid Background */}
        <div className="absolute inset-0 grid-bg opacity-50 z-10" />

        {/* Content */}
        <div className="relative z-20 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
          <div className="max-w-5xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-8 opacity-0 animate-fade-up">
              Software Engineering
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Engineering
              <br />
              <span className="text-mint">Intelligent</span>
              <br />
              Systems.
            </h1>
            <p className="mt-10 text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl opacity-0 animate-fade-up animate-delay-200">
              We build scalable software products and high-performance digital systems for organizations that demand excellence.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-up animate-delay-300">
              <Link to="/products">
                <button 
                  data-testid="hero-explore-products-btn"
                  className="btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors duration-300"
                >
                  Explore Products
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </Link>
              <Link to="/studio">
                <button 
                  data-testid="hero-work-with-us-btn"
                  className="group flex items-center gap-3 bg-transparent border border-white/20 text-white px-8 py-5 font-medium text-sm tracking-wide uppercase hover:bg-white/5 hover:border-mint/50 transition-all duration-300"
                >
                  Work With Us
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-24 grid grid-cols-3 gap-8 max-w-2xl opacity-0 animate-fade-up animate-delay-400">
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-4xl md:text-5xl font-heading font-bold text-white number-display">
                  {stat.number}
                </p>
                <p className="mt-2 text-xs font-mono uppercase tracking-wider text-slate-500">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 opacity-0 animate-fade-in animate-delay-500">
          <div className="w-[1px] h-16 bg-gradient-to-b from-mint/50 to-transparent" />
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="what-we-do-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Products */}
            <div className="group">
              <div className="relative overflow-hidden mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1647356191320-d7a1f80ca777?auto=format&fit=crop&w=800&q=80"
                  alt="Data visualization"
                  className="w-full h-64 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
              </div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Products
              </p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white tracking-tight mb-6">
                Product Development
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                We build proprietary platforms and scalable systems that solve complex problems. Our products serve organizations across fintech, cloud infrastructure, and enterprise software.
              </p>
              <Link 
                to="/products"
                className="inline-flex items-center gap-2 text-mint font-medium hover:text-white transition-colors group/link"
                data-testid="link-view-products"
              >
                View Products
                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Studio */}
            <div className="group lg:mt-16">
              <div className="relative overflow-hidden mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1705073703451-4d49f5eed883?auto=format&fit=crop&w=800&q=80"
                  alt="Abstract metal sculpture"
                  className="w-full h-64 object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent" />
              </div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-coral mb-4">
                Services
              </p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white tracking-tight mb-6">
                Raindas Studio
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Custom enterprise software, web platforms, and technical consulting. We partner with organizations to design and deliver solutions tailored to their specific requirements.
              </p>
              <Link 
                to="/studio"
                className="inline-flex items-center gap-2 text-mint font-medium hover:text-white transition-colors group/link"
                data-testid="link-explore-studio"
              >
                Explore Studio
                <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 md:py-32 bg-obsidian-light" data-testid="capabilities-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Capabilities
              </p>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                What We Build
              </h2>
            </div>
            <p className="text-slate-400 max-w-md text-lg">
              Deep expertise across the full technology stack, from infrastructure to user interfaces.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((cap, index) => (
              <div
                key={cap.title}
                className={`card-beam p-8 group ${index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
                data-testid={`capability-${cap.title.toLowerCase().replace(' ', '-')}`}
              >
                <cap.icon className={`${index === 0 ? 'w-12 h-12' : 'w-8 h-8'} text-mint mb-6 group-hover:text-coral transition-colors`} />
                <h3 className={`${index === 0 ? 'text-3xl' : 'text-xl'} font-heading font-bold text-white mb-4 tracking-tight`}>
                  {cap.title}
                </h3>
                <p className={`text-slate-400 ${index === 0 ? 'text-lg max-w-md' : ''}`}>
                  {cap.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 border-t border-white/5 relative overflow-hidden" data-testid="philosophy-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1535391879778-3bae11d29a24?auto=format&fit=crop&w=2000&q=80"
            alt="Futuristic city"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/90 to-obsidian/80" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                Philosophy
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight leading-[1.1]">
                Engineering
                <br />
                <span className="text-slate-500">Discipline</span>
              </h2>
              <p className="mt-10 text-xl text-slate-300 leading-relaxed max-w-xl">
                Raindas operates at the intersection of clarity and engineering discipline. We design systems built to scale, adapt, and endure.
              </p>
              <p className="mt-6 text-slate-400 leading-relaxed max-w-xl">
                Our approach prioritizes long-term maintainability over short-term convenience. Every architectural decision is evaluated against the requirements of reliability, security, and operational efficiency.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="glass-card p-8">
                <div className="space-y-8">
                  {[
                    { title: "Precision", desc: "Every component serves a purpose", color: "bg-mint" },
                    { title: "Scalability", desc: "Architecture that grows with demand", color: "bg-coral" },
                    { title: "Resilience", desc: "Systems designed to endure", color: "bg-white" }
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className={`w-3 h-3 mt-1.5 ${item.color}`} />
                      <div>
                        <h4 className="text-white font-bold text-lg">{item.title}</h4>
                        <p className="text-slate-400 text-sm mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      {!loading && caseStudies.length > 0 && (
        <section className="py-24 md:py-32 bg-obsidian-light" data-testid="featured-case-studies-section">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                  Case Studies
                </p>
                <h2 className="text-4xl md:text-5xl font-heading font-bold text-white tracking-tight">
                  Recent Work
                </h2>
              </div>
              <Link 
                to="/case-studies"
                className="hidden md:inline-flex items-center gap-2 text-mint font-medium hover:text-white transition-colors group"
                data-testid="link-view-all-case-studies"
              >
                View All
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudies.map((study, idx) => (
                <Link
                  key={study.id}
                  to={`/case-studies/${study.slug}`}
                  className={`card-beam p-8 group ${idx === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
                  data-testid={`case-study-card-${study.slug}`}
                >
                  <p className="text-xs font-mono uppercase tracking-wider text-slate-600 mb-4">
                    {study.industry}
                  </p>
                  <h3 className="text-2xl font-heading font-bold text-white mb-4 group-hover:text-mint transition-colors">
                    {study.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed line-clamp-3 mb-6">
                    {study.challenge}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.technologies.slice(0, 3).map((tech) => (
                      <span 
                        key={tech}
                        className="text-xs px-3 py-1 bg-white/5 text-slate-400 border border-white/10"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 border-t border-white/5" data-testid="cta-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white tracking-tighter">
            Ready to build
            <br />
            <span className="text-mint">something?</span>
          </h2>
          <p className="mt-8 text-xl text-slate-400 max-w-xl mx-auto">
            Get in touch to discuss your project requirements.
          </p>
          <Link to="/contact">
            <button 
              data-testid="cta-contact-btn"
              className="mt-12 btn-primary group flex items-center gap-3 bg-mint text-black px-10 py-6 font-bold text-base tracking-wide uppercase hover:bg-white transition-colors duration-300 mx-auto"
            >
              Contact Us
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
